(function(global,factory){
  const api=factory();
  if(typeof module!=='undefined'&&module.exports)module.exports=api;
  if(global?.document)global.SuimokAccountServer=api;
})(typeof window!=='undefined'?window:undefined,function(){
  'use strict';
  const PRIVATE=['orders','shipping','profile'];
  const AUTH_ERRORS=['AUTH_REQUIRED','IDENTITY_CHANGED','INVALID_TOKEN','ACCESS_DENIED','SERVICE_STOPPED'];
  const fail=code=>{throw Object.assign(new Error(code),{code});};
  function configured(win,c,privateFrame=false){
    try{
      const theme=new URL(c.themeOrigin),api=new URL(c.apiOrigin);
      return c.enabled===true&&c.reviewed===true&&c.frameReviewed===true&&c.mode==='server-session'&&
        [theme,api].every(u=>u.protocol==='https:'&&u.origin===u.href.slice(0,-1))&&theme.origin!==api.origin&&
        win.location.origin===(privateFrame?api.origin:theme.origin)&&/^\d{5,30}$/.test(c.shopId)&&
        (!c.previewThemeId||String(win.Shopify?.theme?.id)===String(c.previewThemeId))&&
        (!c.previewThemeRole||win.Shopify?.theme?.role===c.previewThemeRole);
    }catch{return false;}
  }
  class ServerClient{
    constructor(win,config,{privateFrame=false}={}){
      this.win=win;this.config=Object.freeze({...config});this.privateFrame=privateFrame;
      this.kind='server-session';this.session=null;this.epoch=0;this.requests=new Set();
      if(!privateFrame){
        try{for(const key of ['smk:public-auth:v1','smk:public-pkce:v1','smk:public-logout:v1'])win.sessionStorage?.removeItem(key);}catch{/* Cookie authentication needs no JS credential storage. */}
        try{
          if(win.localStorage?.getItem('smk:server-auth-migrated:v1')!==config.apiOrigin){
            win.localStorage?.setItem('smk:public-auth-epoch:v1',win.crypto.randomUUID());
            win.localStorage?.setItem('smk:server-auth-migrated:v1',config.apiOrigin);
          }
        }catch{/* Do not block HttpOnly-cookie auth when optional storage is unavailable. */}
      }
    }
    abort(){this.epoch++;for(const abort of this.requests)abort.abort();this.requests.clear();}
    async fetch(path,{method='GET',body,signal,sessionKey,auth=false}={}){
      const generation=this.epoch,abort=new this.win.AbortController();this.requests.add(abort);
      const stop=()=>abort.abort();signal?.addEventListener('abort',stop,{once:true});if(signal?.aborted)abort.abort();
      const timeout=this.win.setTimeout(stop,10000);
      const headers={};if(sessionKey)headers['X-Smk-Session']=sessionKey;
      if(method==='POST'){
        const csrf=auth?this.session?.authCsrf:this.session?.csrf;
        if(!csrf){this.requests.delete(abort);this.win.clearTimeout(timeout);signal?.removeEventListener('abort',stop);fail('AUTH_REQUIRED');}
        headers['Content-Type']='application/json';headers['X-Smk-Csrf']=csrf;
      }
      try{
        const response=await this.win.fetch(this.config.apiOrigin+path,{method,headers,body:body===undefined?undefined:JSON.stringify(body),credentials:'include',cache:'no-store',redirect:'error',referrerPolicy:'no-referrer',signal:abort.signal});
        const data=await response.json();
        if(abort.signal.aborted||generation!==this.epoch)fail('IDENTITY_CHANGED');
        if(!response.ok){
          const code=typeof data.code==='string'?data.code:'ACCOUNT_UNAVAILABLE';
          if(method==='POST'&&code==='INVALID_CSRF'){
            // Expired/revoked cookies can fail CSRF before the auth check. Read
            // current state once; never replay the rejected or uncertain POST.
            const previousKey=this.session?.sessionKey;
            try{const current=await this.request('/api/session',{signal:abort.signal});if(!current.authenticated||current.sessionKey!==previousKey){this.abort();this.onInvalidate?.();}}catch{/* Preserve the original error if the read is unavailable. */}
          }
          if(AUTH_ERRORS.includes(code)){this.session=null;this.abort();this.onInvalidate?.(code);}
          fail(code);
        }
        return data;
      }catch(error){if(error.code)throw error;fail(method==='POST'?'WRITE_RESULT_UNKNOWN':'ACCOUNT_UNAVAILABLE');}
      finally{this.win.clearTimeout(timeout);signal?.removeEventListener('abort',stop);this.requests.delete(abort);}
    }
    async request(path,options={}){
      const url=new URL(path,this.config.apiOrigin),method=options.method||'GET';
      if(url.origin!==this.config.apiOrigin||!path.startsWith('/')||path.startsWith('//')||
        !['/api/session','/api/saved',...(this.privateFrame?['/api/profile','/api/orders']:[])].includes(url.pathname)||
        !['GET','POST'].includes(method)||method==='POST'&&!['/api/saved','/api/profile'].includes(url.pathname))fail('INVALID_REQUEST');
      const data=await this.fetch(url.pathname+url.search,options);
      if(url.pathname==='/api/session'){
        if(![true,false].includes(data.authenticated)||typeof data.csrf!=='string'||typeof data.authCsrf!=='string'||data.authenticated&&typeof data.sessionKey!=='string')fail('ACCOUNT_UNAVAILABLE');
        this.session={authenticated:data.authenticated,csrf:data.csrf,authCsrf:data.authCsrf,sessionKey:data.sessionKey};
      }
      return data;
    }
    async navigate(kind,returnTo){
      if(this.privateFrame)fail('INVALID_REQUEST');
      if(!this.session)await this.request('/api/session');
      if(kind==='logout')this.abort();
      const data=await this.fetch('/account/'+kind,{method:'POST',body:{returnTo},auth:true});
      const url=new URL(data.redirectTo);
      const valid=kind==='login'?url.origin==='https://shopify.com'&&url.pathname===`/authentication/${this.config.shopId}/oauth/authorize`:
        url.href===this.config.apiOrigin+'/account/logout-continue';
      if(!valid||url.username||url.password)fail('INVALID_REDIRECT');
      if(kind==='logout')this.session=null;
      this.win.location.assign(url.href);
    }
    login(path){return this.navigate('login',path);}
    logout(path){return this.navigate('logout',path);}
  }
  // Only public presentation values cross the boundary. No arbitrary CSS, URLs,
  // HTML, credential fields, private data or selectors are accepted by messages.
  const COLOR=/^(?:#[a-f\d]{3,8}|rgba?\([\d., %]+\)|transparent|currentColor)$/i;
  const NUMBER=/^-?\d+(?:\.\d+)?(?:px|ms|s|%)?$/;
  function validStyle(name,value){
    if(typeof value!=='string'||value.length>160)return false;
    if(/^--smk-color-/.test(name)||name==='color'||name==='background-color')return COLOR.test(value);
    if(['--color-background','--color-foreground'].includes(name))return /^\d{1,3}, ?\d{1,3}, ?\d{1,3}$/.test(value);
    if(['--smk-font-ui','--smk-font-ja','--smk-font-latin','--font-body-family','--font-heading-family','font-family'].includes(name))return /^[a-z\d '\",_-]+$/i.test(value);
    if(name==='--smk-ratio-product')return /^\d+\s*\/\s*\d+$/.test(value);
    return /^(?:--smk-space-|--smk-radius-)/.test(name)||['--font-body-scale','--font-heading-scale','--font-body-weight','--font-heading-weight','font-size','line-height','letter-spacing','font-weight','root-size'].includes(name)?NUMBER.test(value):false;
  }
  function presentation(win,host){
    const root=win.getComputedStyle(win.document.documentElement),body=win.getComputedStyle(host),values={};
    for(const name of root){const value=root.getPropertyValue(name).trim();if(name.startsWith('--')&&validStyle(name,value))values[name]=value;}
    for(const name of ['font-family','font-size','line-height','letter-spacing','font-weight','color','background-color']){
      const value=body.getPropertyValue(name);if(validStyle(name,value))values[name]=value;
    }
    values['root-size']=root.fontSize;return values;
  }
  const FIELDS={ready:[],activate:['tab','width','styles','scrollTop','revision','open'],sync:['revision','open'],scroll:['tab','top'],close:[],focus:['direction'],login:[],settings:[],contact:[],invalid:[],checked:[],confirm:['id'],confirmed:['id','accepted']};
  function message(data,channel){
    if(!data||typeof data!=='object'||Array.isArray(data)||data.v!==1||data.channel!==channel||!Object.hasOwn(FIELDS,data.type))return false;
    if(Object.keys(data).some(k=>!['v','channel','type',...FIELDS[data.type]].includes(k)))return false;
    if(data.type==='activate'&&(!PRIVATE.includes(data.tab)||!Number.isFinite(data.width)||data.width<100||data.width>4096||!Number.isSafeInteger(data.revision)||typeof data.open!=='boolean'||!data.styles||typeof data.styles!=='object'||Array.isArray(data.styles)||Object.keys(data.styles).length>80||Object.entries(data.styles).some(([k,v])=>!validStyle(k,v))||data.scrollTop!==undefined&&(!Number.isFinite(data.scrollTop)||data.scrollTop<0||data.scrollTop>100000)))return false;
    if(data.type==='sync'&&(!Number.isSafeInteger(data.revision)||typeof data.open!=='boolean'))return false;
    if(data.type==='scroll'&&(!PRIVATE.includes(data.tab)||!Number.isFinite(data.top)||data.top<0||data.top>100000))return false;
    if(data.type==='focus'&&!['previous','next'].includes(data.direction))return false;
    if(['confirm','confirmed'].includes(data.type)&&!/^[a-f\d-]{36}$/i.test(data.id||''))return false;
    if(data.type==='confirmed'&&typeof data.accepted!=='boolean')return false;
    return true;
  }
  class PrivateFrame{
    constructor(win,page){
      this.win=win;this.page=page;this.host=page.host;this.member=page.member;this.config=this.member.transport.config;this.revision=0;this.identity=null;this.ready=false;
      this.channel=win.crypto.randomUUID();this.viewport=win.document.createElement('section');this.viewport.id='smk-account-private';this.viewport.role='tabpanel';this.viewport.hidden=true;this.viewport.className='smk-account-frame';
      for(const tab of PRIVATE){page.query(`[data-smk-account-panel=${tab}]`).remove();page.query(`[data-smk-account-tab=${tab}]`).setAttribute('aria-controls',this.viewport.id);}
      page.scroller.append(this.viewport);
      win.addEventListener('message',event=>this.receive(event));
      win.addEventListener('resize',()=>this.select(page.activeTab));
      this.observer=new win.MutationObserver(()=>this.sync());this.observer.observe(this.host,{attributes:true,attributeFilter:['class']});
      win.addEventListener('pagehide',()=>this.destroy());
      win.addEventListener('pageshow',event=>{if(event.persisted){this.observer.observe(this.host,{attributes:true,attributeFilter:['class']});this.select(this.page.activeTab);}});
    }
    isPrivate(tab){return PRIVATE.includes(tab);}
    post(type,fields={}){this.frame?.contentWindow?.postMessage({v:1,channel:this.channel,type,...fields},this.config.apiOrigin);}
    sync(){
      const identity=this.member.state+':'+(this.member.key||'');
      if(this.identity!==identity){this.identity=identity;this.revision++;}
      if(this.ready)this.post('sync',{revision:this.revision,open:this.host.classList.contains('is-open')&&this.isPrivate(this.page.activeTab)});
    }
    select(tab,{scrollTop}={}){
      const active=this.isPrivate(tab);this.viewport.hidden=!active;this.page.scroller.classList.toggle('smk-account-frame-active',active);
      if(!active){this.sync();return;}
      this.viewport.setAttribute('aria-labelledby','smk-account-tab-'+tab);
      if(!this.frame){
        const frame=this.win.document.createElement('iframe');this.frame=frame;frame.className='smk-account-frame__content';frame.title='会員情報';frame.tabIndex=0;
        this.viewport.replaceChildren();frame.style.visibility='hidden';
        frame.referrerPolicy='no-referrer';frame.setAttribute('sandbox','allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox');
        frame.src=this.config.apiOrigin+'/ui/drawer#'+this.channel;this.viewport.append(frame);
        frame.addEventListener('error',()=>this.failed());this.loadTimer=this.win.setTimeout(()=>this.failed(),10000);
      }
      this.pendingScroll=Number.isFinite(scrollTop)?scrollTop:this.page.scrollPositions[tab];
      this.sync();if(this.ready)this.activate();
    }
    activate(){
      if(!this.isPrivate(this.page.activeTab))return;
      this.post('activate',{tab:this.page.activeTab,width:this.page.scroller.clientWidth,styles:presentation(this.win,this.host),scrollTop:this.pendingScroll,revision:this.revision,open:this.host.classList.contains('is-open')});this.pendingScroll=undefined;
    }
    receive(event){
      if(event.origin!==this.config.apiOrigin||event.source!==this.frame?.contentWindow||!message(event.data,this.channel))return;
      const data=event.data;
      if(data.type==='ready'){this.win.clearTimeout(this.loadTimer);this.ready=true;this.frame.style.visibility='';this.activate();return;}
      if(!this.host.classList.contains('is-open')||!this.isPrivate(this.page.activeTab))return;
      if(data.type==='scroll')this.page.scrollPositions[data.tab]=data.top;
      if(data.type==='close')this.page.controller.close(true);
      if(data.type==='focus'){
        const target=data.direction==='previous'?this.host.querySelector('.smk-saved__header [data-smk-saved-close]'):
          this.host.querySelector('[data-smk-account-session] [data-smk-member-logout]:not([hidden]),[data-smk-account-session] [data-smk-member-login]:not([hidden])');
        (target||this.page.query(`[data-smk-account-tab=${this.page.activeTab}]`)).focus();
      }
      if(data.type==='login'){
        Promise.resolve(this.member.state==='guest'?this.member.login():this.member.refreshIdentity({background:true})).catch(()=>{});
      }
      if(data.type==='settings')this.win.location.assign(`https://shopify.com/${this.config.shopId}/account/profile`);
      if(data.type==='contact')this.win.location.assign('mailto:index@suimok.com');
      if(data.type==='invalid')this.member.refreshIdentity({background:true});
      if(data.type==='checked')this.member.refreshForeground();
      if(data.type==='confirm')this.post('confirmed',{id:data.id,accepted:this.win.confirm('この住所を削除しますか？')});
    }
    failed(){
      if(this.ready)return;this.win.clearTimeout(this.loadTimer);this.frame?.remove();this.frame=null;
      const box=this.win.document.createElement('div');box.className='smk-saved__body';
      const status=this.win.document.createElement('p');status.className='smk-saved__status';status.role='status';status.textContent='会員情報を確認できませんでした。通信環境を確認し、もう一度お試しください。';
      const retry=this.win.document.createElement('button');retry.type='button';retry.className='smk-saved__text-button';retry.textContent='もう一度確認';retry.addEventListener('click',()=>this.select(this.page.activeTab));box.append(status,retry);this.viewport.replaceChildren(box);
    }
    destroy(){this.win.clearTimeout(this.loadTimer);this.frame?.remove();this.frame=null;this.ready=false;this.observer.disconnect();}
  }
  return {ServerClient,PrivateFrame,configured,message,validStyle,PRIVATE,
    create:(win,c,options={})=>configured(win,c,options.privateFrame)?new ServerClient(win,c,options):null,
    attach:(win,page)=>page.member?.transport?.kind==='server-session'?new PrivateFrame(win,page):null};
});
